import { formatDate, formatDateTime } from './time'

/** Number formatting for readings.
 *
 *  Significant digits vary wildly across this network — a water depth is
 *  0.49 m and a lifetime volume counter is 97,936,896 m³ — so a fixed number
 *  of decimals is wrong for one end or the other.
 */
export function formatValue(value: number | null | undefined,
                            unit?: string): string {
  if (value === null || value === undefined) return '—'
  return `${formatNumber(value)}${unit ? ` ${unit}` : ''}`
}

export function formatNumber(value: number): string {
  const magnitude = Math.abs(value)
  if (magnitude === 0) return '0'
  // A counter in the tens of millions gains nothing from decimals, and the
  // digits push the value out of its column.
  if (magnitude >= 10_000) return value.toLocaleString('en-GB',
    { maximumFractionDigits: 0 })
  if (magnitude >= 100) return value.toFixed(1)
  if (magnitude >= 1) return value.toFixed(2)
  return value.toPrecision(3)
}

export function formatCount(value: number): string {
  return value.toLocaleString('en-GB')
}

/** What a stored reading is, where its name does not say.
 *
 *  Nothing is computed — every chart is stored readings — but the reading
 *  may still not be what its name suggests. Precipitation is a within-day
 *  cumulative count of 0.2 mm tips, so a rainy day is a staircase that drops
 *  to zero at the reset. That looks wrong and is not; saying so is the
 *  difference between a reader trusting the chart and reporting it as
 *  broken.
 */
export function describeKind(kind: string): string | null {
  switch (kind) {
    case 'counter_lifetime':
      return 'a running total that never resets: the stored reading, not '
           + 'a rate. The slope is the flow, not the height.'
    case 'counter_daily':
      return 'a running total that resets once a day. The stored reading '
           + 'climbs and drops; the day\'s total is the value it reaches '
           + 'before the reset.'
    case 'direction':
      return 'a bearing in degrees, so 359 and 1 are two degrees apart.'
    default:
      return null
  }
}

export interface KindNoteEntry { key: string; name: string; note: string }

/** The notes KindNotes draws, one per series whose kind needs one. Keyed by
 *  parameter: the instrument charts relabel their series Ch1, Ch2 …, so a
 *  level chart and a temperature chart each carry a "Ch1", and a name is
 *  not an identity. */
export function kindNoteEntries(
  series: { parameter: string; display_name: string; kind: string }[],
): KindNoteEntry[] {
  return series.flatMap((s) => {
    const note = describeKind(s.kind)
    return note ? [{ key: s.parameter, name: s.display_name, note }] : []
  })
}

/** The series a request asked for and got no points of, by name. */
export function absentNames(
  series: { display_name: string; points: unknown[] }[]): string[] {
  return series.filter((s) => s.points.length === 0).map((s) => s.display_name)
}

/** What a chart says about a selected series with nothing in the window,
 *  rather than leaving it in the legend with no line and no explanation.
 *  Under truncation the MDS explorer's second sentence holds too: every
 *  series is cut at one instant, so an empty one began after it. */
export function absentMessage(names: string[], truncated: boolean): string | null {
  if (!names.length) return null
  const first = `No readings in this window for ${names.join(', ')}.`
  return truncated
    ? `${first} Those channels began reporting later than the period shown above.`
    : first
}

/** True when these series hold no reading that could draw: none at all, or
 *  nothing but blanks. Such a chart is not drawn: the window says so instead
 *  (ChartFrame), and ECharts gets no chart whose slider it would label NaN. */
export function drawsNothing(series: { points: readonly (readonly unknown[])[] }[]): boolean {
  return series.every((s) => s.points.every((point) => point[1] === null))
}

/** What stands in place of a chart `drawsNothing` holds back. */
export function emptyWindowMessage(series: { display_name: string }[]): string {
  return absentMessage(series.map((s) => s.display_name), false) ?? 'No readings in this window.'
}

/** A reading at exactly the precision the gateway published.
 *
 *  formatNumber rounds by magnitude, which suits the MDS network and is
 *  wrong for the gateways in both directions at once: it renders a GNSS
 *  latitude of 42.135766894 as 42.14 — about a kilometre, on nodes whose
 *  purpose is millimetre deformation — and a carrier-to-noise the gateway
 *  published as 46 as 46.00, a precision never measured. The decimal count
 *  is recorded per channel at provisioning (station_channel.precision) and
 *  is itself data, so it is honoured exactly: no fewer, no more.
 *
 *  Per channel, not per reading: a channel that reports hundredths shows
 *  13.60 for a reading the gateway printed as 13.6, because the trailing
 *  zero was the printer's, not the sensor's. No thousands separators — the
 *  digits are the reading.
 */
export function formatAtPrecision(value: number, decimals: number): string {
  const places = Math.min(Math.max(Math.trunc(decimals), 0), 20)
  return value.toFixed(places)
}

/** The value as the readings table shows it: at the channel's recorded
 *  precision where there is one, and through formatNumber where there is
 *  not, which is every MDS channel. */
export function displayValue(value: number | null | undefined,
                             precision: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  if (precision === null || precision === undefined) return formatNumber(value)
  return formatAtPrecision(value, precision)
}

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}

/** Where a truncated series actually stops, in the one sentence every
 *  truncation notice in the app uses: the honest failure mode is naming the
 *  period on screen, not merely announcing that a limit exists. */
export function truncationMessage(from: Date, truncatedAt: string): string {
  return `showing ${formatDate(from)} to ${formatDateTime(truncatedAt)}, `
    + 'the point limit. Narrow the range.'
}

/** Text to put inside a tooltip formatter's HTML.
 *
 *  ECharts assigns a formatter's return value to `innerHTML`, and the names
 *  and units that go into ours are typed into the admin panel. Unescaped, a
 *  parameter called `<img onerror=…>` runs script for every viewer who hovers
 *  a chart. Everything interpolated from data goes through this; the markup
 *  we write ourselves, and ECharts' own `marker`, do not. */
export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] ?? char)
}

/** A statistic worked out from readings, which is not a stored reading: at most
 *  six significant digits, so a mean reads 12.3333 and not 12.333333333333334.
 *  A stored reading keeps every digit (displayValue, or String in a tooltip). */
export function formatStatistic(value: number): string {
  return String(Number(value.toPrecision(6)))
}
